import { create } from 'zustand';
import type { UserPreset, ShareablePresetData, CcSnapshotEntry } from '~/types/user-preset';
import type { DevicePattern, MidiCC } from '~/types';

const STORAGE_KEY = 'yomidi-user-presets';
const MAX_NAME_LENGTH = 200;
const VALID_DEVICE_TYPES: UserPreset['deviceType'][] = ['FMSynth', 'MonoSynth', 'MIDI'];

interface PresetStoreState {
  presets: UserPreset[];

  // CRUD
  savePreset: (input: { name: string; deviceType: UserPreset['deviceType']; pattern: DevicePattern; ccSnapshot: CcSnapshotEntry[] }) => UserPreset;
  getPresetById: (id: string) => UserPreset | undefined;
  deletePreset: (id: string) => boolean;
  updatePreset: (id: string, updates: Partial<Pick<UserPreset, 'name'>>) => UserPreset | undefined;
  overwritePreset: (id: string, data: { pattern: DevicePattern; ccSnapshot: CcSnapshotEntry[] }) => UserPreset | undefined;

  // Storage
  loadFromStorage: () => void;
  reset: () => void;

  // Sharing
  encodePresetForUrl: (data: ShareablePresetData) => string;
  decodePresetFromUrl: (encoded: string) => ShareablePresetData | null;
  generateShareUrl: (data: ShareablePresetData) => string;
  importFromUrl: (encoded: string) => UserPreset | null;

  // Create from device
  createPresetFromDevice: (input: { name: string; deviceType: UserPreset['deviceType']; pattern: DevicePattern; ccs: MidiCC[] }) => UserPreset;
}

function generateId(): string {
  return `preset-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function deepCopyPattern(pattern: DevicePattern): DevicePattern {
  return JSON.parse(JSON.stringify(pattern));
}

function persistToStorage(presets: UserPreset[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
  } catch (err) {
    console.warn('[PRESETS] Failed to persist presets:', err);
  }
}

function loadPresetsFromStorage(): UserPreset[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

/**
 * UTF-8-safe base64url encoding (replaces deprecated escape/unescape).
 */
function encodeBase64Url(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  bytes.forEach(b => { binary += String.fromCharCode(b); });
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeBase64Url(encoded: string): string {
  let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4 !== 0) base64 += '=';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/**
 * Validate a shared preset data structure. Returns true if safe to use.
 */
function isValidShareablePreset(data: unknown): data is ShareablePresetData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // Name
  if (typeof d.name !== 'string' || d.name.length === 0 || d.name.length > MAX_NAME_LENGTH) return false;

  // Device type whitelist
  if (typeof d.deviceType !== 'string') return false;
  if (!VALID_DEVICE_TYPES.includes(d.deviceType as UserPreset['deviceType'])) return false;

  // Pattern structure
  if (!isValidPattern(d.pattern)) return false;

  // CC snapshot
  if (!Array.isArray(d.ccSnapshot)) return false;
  for (const cc of d.ccSnapshot) {
    if (!cc || typeof cc !== 'object') return false;
    const c = cc as Record<string, unknown>;
    if (typeof c.number !== 'number' || !Number.isInteger(c.number) || c.number < 0 || c.number > 127) return false;
    if (typeof c.value !== 'number' || !Number.isInteger(c.value) || c.value < 0 || c.value > 127) return false;
  }

  return true;
}

function isValidPattern(pattern: unknown): pattern is DevicePattern {
  if (!pattern || typeof pattern !== 'object') return false;
  const p = pattern as Record<string, unknown>;

  // Required nested objects
  if (!p.rootNote || typeof p.rootNote !== 'object') return false;
  if (!p.scale || typeof p.scale !== 'object') return false;
  if (p.scaleMode !== 'Major' && p.scaleMode !== 'Minor') return false;
  if (!p.pattern || typeof p.pattern !== 'object') return false;
  if (!p.patternParts || typeof p.patternParts !== 'object') return false;
  if (!p.scrambleToggles || typeof p.scrambleToggles !== 'object') return false;

  // Pattern parts must have A and B arrays
  const parts = p.patternParts as Record<string, unknown>;
  if (!Array.isArray(parts.A) || !Array.isArray(parts.B)) return false;

  // Numeric fields
  const numericFields = [
    'beatsPerBar', 'tempo', 'swing',
    'space', 'spaceMin', 'spaceMax',
    'dissonance', 'dissonanceMin', 'dissonanceMax',
    'spread', 'spreadMin', 'spreadMax',
    'range', 'rangeMin', 'rangeMax',
  ];
  for (const field of numericFields) {
    if (typeof p[field] !== 'number' || !Number.isFinite(p[field] as number)) return false;
  }

  return true;
}

function validatePresetInput(input: { name: string; deviceType: UserPreset['deviceType'] }): void {
  if (!input.name || typeof input.name !== 'string' || input.name.length === 0) {
    throw new Error('Preset name cannot be empty');
  }
  if (input.name.length > MAX_NAME_LENGTH) {
    throw new Error(`Preset name cannot exceed ${MAX_NAME_LENGTH} characters`);
  }
  if (!VALID_DEVICE_TYPES.includes(input.deviceType)) {
    throw new Error(`Invalid deviceType: ${input.deviceType}`);
  }
}

export const usePresetStore = create<PresetStoreState>()((set, get) => ({
  // Hydrate from localStorage immediately so fresh instances see saved data.
  presets: loadPresetsFromStorage(),

  savePreset: (input) => {
    validatePresetInput(input);

    const now = new Date().toISOString();
    const preset: UserPreset = {
      id: generateId(),
      name: input.name,
      deviceType: input.deviceType,
      pattern: deepCopyPattern(input.pattern),
      ccSnapshot: input.ccSnapshot.map(cc => ({ ...cc })),
      createdAt: now,
      updatedAt: now,
    };

    // Merge with current localStorage (not just in-memory state) to avoid clobbering
    // writes made by other tabs or stale in-memory state after reset().
    const persistedPresets = loadPresetsFromStorage();
    const inMemoryPresets = get().presets;
    const mergedById = new Map<string, UserPreset>();
    for (const p of persistedPresets) mergedById.set(p.id, p);
    for (const p of inMemoryPresets) mergedById.set(p.id, p);
    mergedById.set(preset.id, preset);
    const merged = Array.from(mergedById.values());

    persistToStorage(merged);
    set({ presets: merged });

    return preset;
  },

  getPresetById: (id) => {
    return get().presets.find(p => p.id === id);
  },

  deletePreset: (id) => {
    const exists = get().presets.some(p => p.id === id);
    if (!exists) return false;

    set((state) => {
      const updated = state.presets.filter(p => p.id !== id);
      persistToStorage(updated);
      return { presets: updated };
    });
    return true;
  },

  updatePreset: (id, updates) => {
    const preset = get().presets.find(p => p.id === id);
    if (!preset) return undefined;

    if (updates.name !== undefined) {
      validatePresetInput({ name: updates.name, deviceType: preset.deviceType });
    }

    const updated: UserPreset = {
      ...preset,
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    set((state) => {
      const newPresets = state.presets.map(p => p.id === id ? updated : p);
      persistToStorage(newPresets);
      return { presets: newPresets };
    });

    return updated;
  },

  overwritePreset: (id, data) => {
    const preset = get().presets.find(p => p.id === id);
    if (!preset) return undefined;

    const updated: UserPreset = {
      ...preset,
      pattern: deepCopyPattern(data.pattern),
      ccSnapshot: data.ccSnapshot.map(cc => ({ ...cc })),
      updatedAt: new Date().toISOString(),
    };

    set((state) => {
      const newPresets = state.presets.map(p => p.id === id ? updated : p);
      persistToStorage(newPresets);
      return { presets: newPresets };
    });

    return updated;
  },

  loadFromStorage: () => {
    const presets = loadPresetsFromStorage();
    set({ presets });
  },

  reset: () => {
    set({ presets: [] });
  },

  encodePresetForUrl: (data) => {
    try {
      return encodeBase64Url(JSON.stringify(data));
    } catch {
      return '';
    }
  },

  decodePresetFromUrl: (encoded) => {
    if (!encoded) return null;
    try {
      const json = decodeBase64Url(encoded);
      const data = JSON.parse(json);
      if (!isValidShareablePreset(data)) return null;
      return data;
    } catch {
      return null;
    }
  },

  generateShareUrl: (data) => {
    const encoded = get().encodePresetForUrl(data);
    return `/share/${encoded}`;
  },

  importFromUrl: (encoded) => {
    const data = get().decodePresetFromUrl(encoded);
    if (!data) return null;

    return get().savePreset({
      name: data.name,
      deviceType: data.deviceType,
      pattern: data.pattern,
      ccSnapshot: data.ccSnapshot,
    });
  },

  createPresetFromDevice: (input) => {
    const ccSnapshot: CcSnapshotEntry[] = input.ccs
      .filter(cc => cc.value !== null)
      .map(cc => ({ number: cc.number, value: cc.value as number }));

    return get().savePreset({
      name: input.name,
      deviceType: input.deviceType,
      pattern: input.pattern,
      ccSnapshot,
    });
  },
}));
